import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { showModal } from '../../store/actions/modalActions'; 
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { PiCaretDownLight, PiWarningCircleLight } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import ProfileImage from '../Header/ProfileImage';
import { getInitials } from '../shared/TailwindComponents/CollaboratorsBadge';
import apis from '../../utils/apis';
import { isBetaEnv } from '../../utils/authUtils';
import Badge from '../shared/TailwindComponents/Badge';


const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 60,
            damping: 12,
        },
    },
    exit: { opacity: 0, y: 30, transition: { duration: 0.2 } },
} as const;

const stepVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: (i: number) => ({
        opacity: 1,
        x: 0,
        transition: { delay: i * 0.1 },
    }),
};

const OnboardingWidget: React.FC = () => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const history = useHistory();
    const dispatch = useAppDispatch();
    const userInfo = useSelector((state: RootState) => state.user.userInfo)
    const [steps, setSteps] = useState<any[]>([]);
    const [isCompleted, setIsCompleted] = useState(null)


    // const fetchSteps = async () => {
    //     try {
    //         const res = await apis.get('/user/onboarding/checklist'); 
    //         setSteps(res.data.steps || []);
    //         setIsCompleted(res.data.completed)
    //     } catch (error) {
    //         console.error('Failed to fetch onboarding steps:', error);
    //     } 
    // };

    // useEffect(() => {

    //     fetchSteps();
    // }, []);


    const dummyUser = {
        userId: userInfo.UserID,
        name: `${userInfo.FirstName} ${userInfo.LastName}`,
        avatar: userInfo.ProfilePicture,
        role: 'Managing Partner',
    };

    const relevantSteps = steps
    const completedCount = relevantSteps.filter(s => s.status === "DONE").length;
    const percentage = Math.round((completedCount / relevantSteps.length) * 100);

    const handleStepClick = async (step: any) => {

        if (step.modal) {
            dispatch(showModal({
                showModal: true,
                type: 'settings',
                message: '',
                duration: 0,
                onConfirm: () => { },
                onCancel: () => { },
                inputData: step.settingName,
            }));
        } else if (step.path) {
            history.push(step.path);
        }

        if (step.status === "PENDING") {
            await apis({
                "method": "POST",
                url: "/user/onboarding/checklist/start",
                data: {
                    step_name: step.name,
                }
            });

            // await fetchSteps()
        }
    };

    return (
        <div>
            {!isCompleted && !open && steps.length > 0 && !isBetaEnv && (
                <button
                    onClick={() => setOpen(true)}
                    className="bg-primary-600 text-white rounded-full text-xs hover:bg-primary-700"
                >
                    <Badge color='black'>{percentage}%</Badge>
                </button>
            )}

            <AnimatePresence>
                {open && (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="fixed bottom-4 left-6 bg-white w-[95vw] max-w-[500px] max-h-[90vh] rounded-2xl shadow-2xl p-1 sm:p-0 overflow-y-auto border border-gray-200 z-50"
                    >
                        <div
                            className='flex flex-1 justify-between items-center bg-secondary-900 px-2 py-2'
                        >
                            <span className='text-white '>{t('Onboarding Checklist')}</span>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-gray-400 text-xl hover:text-gray-600 mr-3"
                            >
                                <PiCaretDownLight className='text-white-400' />
                            </button>
                        </div>
                        <div className="flex  items-center justify-between mb-1 px-6 py-4">
                            <div className="flex flex-1 items-center gap-9">
                                <div className="relative w-30 h-30">
                                    <svg className="absolute top-0 left-0 w-30 h-30 transform rotate-[90deg]" viewBox="0 0 100 100">
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="45"
                                            stroke="#e5e7eb"  
                                            strokeWidth="1"
                                            fill="transparent"
                                        />
                                        <circle
                                            cx="50"
                                            cy="52"
                                            r="45"
                                            stroke="#10b981" 
                                            strokeWidth="5"
                                            fill="transparent"
                                            strokeDasharray="282.6"
                                            strokeDashoffset={282.6 - (percentage / 100) * 282.6}
                                            strokeLinecap="round"
                                            className="transition-all duration-700"
                                        />
                                    </svg>
                                    <div
                                        className={`w-30 h-30 rounded-full p-1 transition-colors duration-300' }`}
                                    >
                                        <ProfileImage
                                            key={dummyUser.userId}
                                            profilePicture={dummyUser.avatar}
                                            email={userInfo.Email}
                                            initials={getInitials(dummyUser.name)}
                                            name={dummyUser.name}
                                            size="w-[110px] h-[110px] rounded-full border-2 border-white object-cover"
                                        />
                                    </div>

                                    <span className="absolute bottom-0 right-2 bg-success-100 text-success-600 text-[10px] font-semibold px-2 py-0.5 rounded-[15px] shadow-sm border border-success-500 whitespace-nowrap uppercase">
                                        {t('{{percentage}}% Completed', { percentage })}
                                    </span>
                                </div>

                                <div className="flex-1 items-end">
                                    <p className="text-sm text-gray-500">{t('Welcome back,')}</p>
                                    <p className="text-xl font-bold text-gray-800">{dummyUser.name}</p>
                                    <div className="flex items-start justify-end mt-1 gap-1">
                                        <PiWarningCircleLight className="text-gray-700 -mt-1 -ml-4 " size={30} />
                                        <p className="text-xs text-gray-600 leading-relaxed max-w-xs text-left">
                                            {t('Please ensure you complete all the items below to avoid any delays/blocker to your billings and payouts.')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="w-full bg-gray-200 h-1 rounded-full mb-4">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 0.6, ease: 'easeInOut' }}
                                className="bg-success-500 h-1 rounded-full"
                            />
                        </div>

                        <div className="space-y-4 px-2 py-1 overflow-auto max-h-[60vh] min-h-[50vh] mb-4">
                            {relevantSteps.map((step, i) => (
                                <motion.div
                                    key={step.id}
                                    custom={i}
                                    variants={stepVariants}
                                    initial="hidden"
                                    animate="visible"
                                    onClick={() => handleStepClick(step)}
                                    className={`cursor-pointer p-4 rounded-xl border transition-all duration-200 ${step.status === "DONE"
                                        ? 'bg-success-50 border-success-400'
                                        : step.status === "IN_PROGRESS" ? 'bg-warning-50 border-warning-400' : 'bg-white hover:bg-gray-50 border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="font-medium text-base text-gray-800">{step.name}</p>
                                        <span
                                            className={`text-lg font-semibold ${step.status === "DONE" ? 'text-success-600' : step.status === "IN_PROGRESS" ? "text-warning-400" : 'text-gray-400'
                                                }`}
                                        >
                                            {step.status === "DONE" ? '✔' : step.status === "IN_PROGRESS" ? '!' : '○'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">{step.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default OnboardingWidget;
